import Swiper, { Navigation, Pagination, Scrollbar, EffectFade, EffectCreative, Autoplay } from 'swiper'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
Swiper.use([Navigation, Pagination, Scrollbar, EffectFade, EffectCreative, Autoplay])

// Set default Swiper options
const defaultSwiperOptions = {
  direction: 'horizontal',
  slidesPerView: 1,
  spaceBetween: 20,
  speed: 500,
  loop: true,
  centeredSlidesBound: true,
  centerInsufficientSlides: true,
  watchOverflow: true,
  watchSlidesProgress: true,
  watchSlidesVisibility: true,
  slideVisibleClass: 'swiper-slide-visible',
  a11y: {
    prevSlideMessage: 'Previous slide',
    nextSlideMessage: 'Next slide',
    firstSlideMessage: 'First slide',
    lastSlideMessage: 'Last slide'
  },
  on: {
    init: function (swiper) {
      swiper.wf = {}
      wfInitializeNavbar(swiper)
      wfUpdateActiveNavbarButton(swiper)
      wfInitializeBackground(swiper)
      wfUpdateActiveBackgroundItem(swiper)
      wfInitializeContentSync(swiper)
      wfUpdateActiveContentSyncItem(swiper)
      wfInitializeNavButtons(swiper)
      wfAwardsAriaLabels(swiper)
    },
    slideChange: function (swiper) {
      wfUpdateActiveNavbarButton(swiper)
      wfUpdateActiveBackgroundItem(swiper)
      wfUpdateActiveContentSyncItem(swiper)
      wfUpdateButtonAriaLabel(swiper)
    }
  }
}

function wfAwardsAriaLabels(swiperInstance) {
  const wfPageBullets = swiperInstance.el.closest('.widget.awards')?.querySelectorAll('.swiper-pagination')

  if (!wfPageBullets) {
    return;
  }

  wfPageBullets.forEach(bullet => {
    let childNodes = bullet.childNodes;
    if (childNodes.length > 0) {
      for (let i = 0; i < childNodes.length; i++) {
        childNodes[i].setAttribute('aria-label', `Slide ${i + 1} of ${childNodes.length}`)
        childNodes[i].setAttribute('role', 'button')
      }
    }

  });

}

// Register and initialize Swiper navbar
function wfInitializeNavbar(swiperInstance) {
  const wfSwiperNavbars = swiperInstance.el.closest('.widget')?.querySelectorAll('.swiper-navbar')
  if (wfSwiperNavbars?.length > 0) {
    swiperInstance.wf.SwiperNavbars = wfSwiperNavbars
    for (let i = 0; i < swiperInstance.wf.SwiperNavbars.length; i++) {
      let allNavbarButtons = swiperInstance.wf.SwiperNavbars[i].querySelectorAll('.swiper-navbar-button')
      allNavbarButtons.forEach((b, i) => {
        b.addEventListener('click', function () {
          swiperInstance.slideTo(i)
        })
      })
    }
  }
}

// If Swiper navbar exists, then mark the current slide's navbar button as active
function wfUpdateActiveNavbarButton(swiperInstance) {
  if (swiperInstance.wf && swiperInstance.wf.SwiperNavbars) {
    for (let i = 0; i < swiperInstance.wf.SwiperNavbars.length; i++) {
      let allNavbarButtons = swiperInstance.wf.SwiperNavbars[i].querySelectorAll('.swiper-navbar-button')
      let currentNavbarButton = allNavbarButtons.item(swiperInstance.realIndex)
      allNavbarButtons.forEach(t => t.classList.remove('swiper-navbar-button-active'))
      if (currentNavbarButton) {
        currentNavbarButton.classList.add('swiper-navbar-button-active')
      }
    }
  }
}

// Register and initialize Swiper background
function wfInitializeBackground(swiperInstance) {
  const wfSwiperBackground = swiperInstance.el.closest('.widget')?.querySelector('.swiper-background')
  if (wfSwiperBackground) {
    swiperInstance.wf.SwiperBackground = wfSwiperBackground
  }
}

// If Swiper background exists, then mark the current slide's background item as active
function wfUpdateActiveBackgroundItem(swiperInstance) {
  if (swiperInstance.wf && swiperInstance.wf.SwiperBackground) {
    const allBackgroundItems = swiperInstance.wf.SwiperBackground.querySelectorAll('.swiper-background-item')
    const currentBackgroundItem = allBackgroundItems.item(swiperInstance.realIndex)
    allBackgroundItems.forEach(t => t.classList.remove('swiper-background-item-active'))
    if (currentBackgroundItem) {
      currentBackgroundItem.classList.add('swiper-background-item-active')
    }
  }
}

// Register and initialize Swiper content sync
function wfInitializeContentSync(swiperInstance) {
  const wfSwiperContentSync = swiperInstance.el.closest('.widget')?.querySelectorAll('.swiper-content-sync')
  if (wfSwiperContentSync) {
    swiperInstance.wf.wfSwiperContentSync = wfSwiperContentSync
  }
}

// If Swiper content sync exists, then mark the current slide's content sync items as active
function wfUpdateActiveContentSyncItem(swiperInstance) {
  if (swiperInstance.wf && swiperInstance.wf.wfSwiperContentSync) {
    swiperInstance.wf.wfSwiperContentSync.forEach(sync => {
      const allSyncItems = sync.querySelectorAll('.swiper-content-sync-item')
      const currentSyncItem = allSyncItems.item(swiperInstance.realIndex)
      allSyncItems.forEach(t => t.classList.remove('active'))
      if (currentSyncItem) {
        currentSyncItem.classList.add('active')
      }
    })
  }
}

function wfUpdateButtonAriaLabel(swiperInstance) {
  const wfSwiperElement = swiperInstance.el.closest('.widget')

  if (!wfSwiperElement) {
    return;
  }

  const wfSwiperNavPrevButtons = wfSwiperElement?.querySelector('.swiper-button-prev')
  const wfSwiperNavNextButtons = wfSwiperElement?.querySelector('.swiper-button-next')

  // If the swiper element is a spotlight banner or awards slider, then don't run the aria label generation
  if (wfSwiperElement.classList.contains('spotlight-banner')) {
    return;
  }
  if (wfSwiperElement.classList.contains('awards-slider')) {
    return;
  }

  if (!wfSwiperNavPrevButtons || !wfSwiperNavNextButtons) {
    return;
  }

  const swiperSlides = swiperInstance.el.closest('.widget')?.querySelector('.swiper').dataset.slidelength

  let prevSlide = swiperInstance.el.closest('.widget')?.querySelector('.swiper-slide-prev')
  let nextSlide = swiperInstance.el.closest('.widget')?.querySelector('.swiper-slide-next')

  if (prevSlide) {
    if (prevSlide.dataset.slideindex === swiperSlides) {
      // Loop around
      wfSwiperNavPrevButtons.setAttribute('aria-label', `Previous Slide: ${swiperSlides} of ${swiperSlides}`)
    } else {
      wfSwiperNavPrevButtons.setAttribute('aria-label', `Previous Slide: ${prevSlide.dataset.slideindex} of ${swiperSlides}`)
    }

  }

  if (nextSlide) {
    if (nextSlide.dataset.slideindex === '1') {
      // Loop around
      wfSwiperNavNextButtons.setAttribute('aria-label', `Next Slide: 1 of ${swiperSlides}`)
    } else {
      wfSwiperNavNextButtons.setAttribute('aria-label', `Next Slide: ${nextSlide.dataset.slideindex} of ${swiperSlides}`)
    }

  }

}

// Register and initialize nav buttons
function wfInitializeNavButtons(swiperInstance) {
  const wfSwiperElement = swiperInstance.el.closest('.widget')
  const wfSwiperNavPrevButtons = wfSwiperElement?.querySelectorAll('.swiper-button-prev')
  const wfSwiperNavNextButtons = wfSwiperElement?.querySelectorAll('.swiper-button-next')
  const wfSwiperNavPlayButtons = wfSwiperElement?.querySelectorAll('.swiper-button-play')
  const wfSwiperNavPauseButtons = wfSwiperElement?.querySelectorAll('.swiper-button-pause')
  const swiperSlides = wfSwiperElement?.querySelector('.swiper').dataset.slidelength

  if (!wfSwiperElement) {
    return
  }

  // If the swiper element is a spotlight banner or awards slider, then don't run the aria label generation
  if (wfSwiperElement.classList.contains('spotlight-banner')) {
    return;
  }
  if (wfSwiperElement.classList.contains('awards-slider')) {
    return;
  }
  if (wfSwiperElement.classList.contains('featured-content')) {
    return;
  }

  // Previous Buttons
  wfSwiperNavPrevButtons?.forEach(button => {

    // Initialize default aria label 
    button.setAttribute('aria-label', `Previous Slide: ${swiperSlides} of ${swiperSlides}`)

    button.addEventListener('click', function (event) {

      let nextButton = button.nextElementSibling
      let prevSlide = wfSwiperElement?.querySelector('.swiper-slide-prev')
      let nextSlide = wfSwiperElement?.querySelector('.swiper-slide-next')

      if (prevSlide.dataset.slideindex === nextSlide.dataset.slideindex) {
        // This provides the correct index for the duplicate slide
        let previousSlideInt = parseInt(swiperSlides) - 1

        button.setAttribute('aria-label', `Previous Slide: ${previousSlideInt} of ${swiperSlides}`)
        nextButton.setAttribute('aria-label', `Next Slide: 1 of ${swiperSlides}`)

      } else {
        if (prevSlide) {
          if (prevSlide.dataset.slideindex === swiperSlides) {
            // Loop around
            button.setAttribute('aria-label', `Previous Slide: ${swiperSlides} of ${swiperSlides}`)
          }
          else {
            button.setAttribute('aria-label', `Previous Slide: ${prevSlide.dataset.slideindex} of ${swiperSlides}`)
          }

        }

        if (nextSlide) {
          if (nextSlide.dataset.slideindex === '1') {
            // Loop around
            nextButton.setAttribute('aria-label', `Next Slide: 1 of ${swiperSlides}`)
          } else {
            nextButton.setAttribute('aria-label', `Next Slide: ${nextSlide.dataset.slideindex} of ${swiperSlides}`)
          }

        }
      }

    })
  })

  //Next Buttons
  wfSwiperNavNextButtons?.forEach(button => {
    let nextSlide = swiperInstance.el.closest('.widget')?.querySelector('.swiper-slide-next')
    if (!nextSlide) {
      return;
    }
    button.setAttribute('aria-label', `Next Slide: ${nextSlide.dataset.slideindex} of ${swiperSlides}`)

    button.addEventListener('click', function (event) {

      let prevButton = button.previousElementSibling
      let prevSlide = wfSwiperElement?.querySelector('.swiper-slide-prev')
      let nextSlide = wfSwiperElement?.querySelector('.swiper-slide-next')


      if (prevSlide.dataset.slideindex === nextSlide.dataset.slideindex) {
        // This provides the correct index for the duplicate slide
        let nextSlideInt = 1

        if (parseInt(swiperSlides) > 0) {
          nextSlideInt++
        }

        prevButton.setAttribute('aria-label', `Previous Slide: ${swiperSlides} of ${swiperSlides}`)
        button.setAttribute('aria-label', `Next Slide: ${nextSlideInt} of ${swiperSlides}`)

      } else {
        if (prevSlide) {
          if (prevSlide.dataset.slideindex === swiperSlides) {
            // Loop around
            prevButton.setAttribute('aria-label', `Previous Slide: ${swiperSlides} of ${swiperSlides}`)
          } else {
            prevButton.setAttribute('aria-label', `Previous Slide ${prevSlide.dataset.slideindex} of ${swiperSlides}`)
          }
        }

        if (nextSlide) {
          if (nextSlide.dataset.slideindex === '1') {
            // Loop around
            button.setAttribute('aria-label', `Next Slide: 1 of ${swiperSlides}`)
          } else {
            button.setAttribute('aria-label', `Next Slide: ${nextSlide.dataset.slideindex} of ${swiperSlides}`)
          }

        }
      }

    })
  })

  // Play buttons
  wfSwiperNavPlayButtons?.forEach(button => {
    button.addEventListener('click', function (event) {
      let pauseButton = button.nextElementSibling
      swiperInstance.autoplay.start()

      button.classList.add('d-none')
      pauseButton.classList.remove('d-none')
      console.log('Slide autoplay has started')
    })
  })


  wfSwiperNavPauseButtons?.forEach(button => {
    button.addEventListener('click', function (event) {
      let playButton = button.previousElementSibling
      swiperInstance.autoplay.stop()

      button.classList.add('d-none')
      playButton.classList.remove('d-none')
      console.log('Slide autoplay has been paused')
    })
  })

}

// Initializes a Swiper instance on the given element with the provided options
function initializeSwiper(wrapperSelector, additionalOptions) {
  const wrapperElements = document.querySelectorAll(wrapperSelector)
  wrapperElements.forEach((wrapperElement) => {
    const swiperElement = wrapperElement.querySelector('.swiper')
    const swiperDataOptions = JSON.parse(swiperElement?.dataset.swiper ?? null)
    const options = { ...defaultSwiperOptions, ...additionalOptions, ...swiperDataOptions }

    // Replace autoplay = true with default autoplay settings
    if (options.autoplay) {
      options.autoplay = {
        delay: 10000
      }
    }

    // Replace the specific pagination and navigation objects with child elements selected from this wrapper
    if (options.pagination) {
      options.pagination = {
        el: wrapperElement.querySelector(options.pagination.el),
        type: options.pagination.type,
        clickable: options.pagination.clickable
      }
    }
    if (options.navigation) {
      options.navigation = {
        prevEl: wrapperElement.querySelector(options.navigation.prevEl),
        nextEl: wrapperElement.querySelector(options.navigation.nextEl)
      }
    }

    const swiper = new Swiper(swiperElement, options)

    // If custom option photoSwipe is set, then initialize PhotoSwipe on this slider
    if (options.photoSwipe) {
      const lightbox = new PhotoSwipeLightbox({
        gallery: swiperElement,
        children: 'a.pswp-item',
        pswpModule: () => import('photoswipe'),
        bgOpacity: 1,
        showHideOpacity: true,
        showHideAnimationType: 'zoom'
      })

      lightbox.on('change', () => {
        swiper.slideTo(lightbox.pswp.currIndex, 0, true)
      })

      lightbox.on('closingAnimationStart', () => {
        swiper.slideTo(lightbox.pswp.currIndex, 0, true)
      })

      lightbox.on('uiRegister', function () {
        lightbox.pswp.ui.registerElement({
          name: 'custom-caption',
          order: 9,
          isButton: false,
          appendTo: 'root',
          html: 'Caption text',
          onInit: (el, pswp) => {
            lightbox.pswp.on('change', () => {
              const slideElement = lightbox.pswp.currSlide.data.element
              let captionHtml = ''
              if (slideElement) {
                const captionContentElement = slideElement.querySelector('.pswp-caption-content')
                if (captionContentElement) {
                  captionHtml = captionContentElement.innerHTML
                }
              }
              el.innerHTML = captionHtml
            })
          }
        })
      })

      lightbox.init()
    }

    return swiper
  })
}

// Initialize Swiper instances
export function initializeSwiperInstances() {
  initializeSwiper(
    '.hero-banner',
    {
      slidesPerView: 1,
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
    }
  )

  initializeSwiper(
    '.content-slider',
    {
      slidesPerView: 1,
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
    }
  )

  initializeSwiper(
    '.spotlight-banner',
    {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
    }
  )

  initializeSwiper(
    '.featured-articles-slider',
    {
      slidesPerView: 'auto',
      loop: false,
      spaceBetween: 0,
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
    }
  )

  initializeSwiper(
    '.newsroom-featured-slider',
    {
      slidesPerView: 'auto',
      loop: true,
      spaceBetween: 0,
      centeredSlides: true,
      effect: 'creative',
      creativeEffect: {
        prev: {
          opacity: .5,
          translate: ['-90%', 0, -600],
        },
        next: {
          opacity: .5,
          translate: ['90%', 0, -600],
        },
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
    }
  )

  initializeSwiper(
    '.bulleted-slider',
    {
      slidesPerView: 1,
      loop: true,
      spaceBetween: 0,
      centeredSlides: true,
      effect: 'slide',
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
      }
    }
  )

  initializeSwiper(
    '.awards-slider',
    {
      slidesPerView: 1,
      loop: false,
      spaceBetween: 0,
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true
      },
    }
  )

  initializeSwiper(
    '.media-gallery-slider',
    {
      slidesPerView: 1,
      loop: false,
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      pagination: {
        el: '.swiper-pagination',
        type: 'fraction',
        clickable: false
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
      photoSwipe: true
    }
  )

  initializeSwiper(
    '.testimonial-slider',
    {
      slidesPerView: 1,
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
    }
  )

  initializeSwiper(
    '.featured-content',
    {
      slidesPerView: 'auto',
      spaceBetween: 30,
      loop: false,
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
      scrollbar: {
        el: '.swiper-scrollbar',
        draggable: true,
        dragSize: 'auto'
      }
    }
  )
}

document.addEventListener('DOMContentLoaded', (event) => {
  initializeSwiperInstances()
})

// News - Featured Display Toggle

const newsFeaturedItems = document.querySelectorAll('.newsroom-featured')
const newsDismissButtons = document.querySelectorAll('.news-dismiss-button')

newsDismissButtons.forEach((button, i) => {
  // make ids dynamic
  const parent = button.closest('.newsroom-featured');
  parent.setAttribute('id', parent.id + i);

  // add click event that stores the href with unique id to track closing
  button?.addEventListener('click', event => {
    const parentDiv = button.closest('.newsroom-featured');
    const parentDivId = parentDiv?.id || 'no-id';
    const combinedKey = `${window.location.href}-${parentDivId}`;
    sessionStorage.setItem(combinedKey, true);

    toggleFeatured(parentDiv);
  })
})

function toggleFeatured(parentDiv) {
  const parentDivId = parentDiv?.id || 'no-id';
  const combinedKey = `${window.location.href}-${parentDivId}`;
  if (sessionStorage.getItem(combinedKey) === 'true') {
    parentDiv.classList.add('visually-hidden')
  } else {
    parentDiv.classList.remove('visually-hidden')
  }
}

newsFeaturedItems.forEach(x => {
  if (x) {
    toggleFeatured(x)
  }
})
